import React, { FC, useMemo, useState } from 'react'
import { format } from 'date-fns'
import { makeStyles } from '@material-ui/core/styles'
import {
  Box,
  Button,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
} from '@material-ui/core'
import { Edit as EditIcon } from '@material-ui/icons'
import { TolNotification } from '../../../../types/tolOperations'
import { PeriodFilterChips } from '../components/PeriodFilterChips'
import {
  ItemPeriodFilter,
  NOTIFICATION_STATUS_LABELS,
  PERIOD_FILTER_OPTIONS,
  getNotificationPublishStatus,
  notificationMatchesFilter,
  parseNotificationDate,
} from '../shared/periodStatus'

const useStyles = makeStyles(theme => ({
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(3),
  },
  table: {
    minWidth: 650,
  },
  emptyState: {
    textAlign: 'center',
    padding: theme.spacing(8),
    color: theme.palette.text.secondary,
  },
  loadingState: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(8),
  },
  infoChip: {
    backgroundColor: '#2196f3',
    color: '#fff',
  },
  warningChip: {
    backgroundColor: '#ff9800',
    color: '#fff',
  },
  urlCell: {
    fontSize: 12,
    maxWidth: 200,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
}))

interface NotificationListPageProps {
  notifications: TolNotification[]
  isLoading: boolean
  onOpenEdit: () => void
}

const formatDisplayDate = (dateString: string): string => {
  const date = parseNotificationDate(dateString)
  return date ? format(date, 'yyyy/MM/dd HH:mm') : dateString
}

export const NotificationListPage: FC<NotificationListPageProps> = ({ notifications, isLoading, onOpenEdit }) => {
  const classes = useStyles()
  const [periodFilter, setPeriodFilter] = useState<ItemPeriodFilter>('current')

  const filterCounts = useMemo(
    () => ({
      current: notifications.filter(n => notificationMatchesFilter(n, 'current')).length,
      ended: notifications.filter(n => notificationMatchesFilter(n, 'ended')).length,
      all: notifications.length,
    }),
    [notifications],
  )

  const filteredNotifications = useMemo(
    () => notifications.filter(n => notificationMatchesFilter(n, periodFilter)),
    [notifications, periodFilter],
  )

  if (isLoading) {
    return (
      <Box className={classes.loadingState}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box>
      <Box className={classes.header}>
        <Typography variant="h5" component="h2">
          お知らせ一覧
        </Typography>
        <Button variant="contained" color="primary" startIcon={<EditIcon />} onClick={onOpenEdit}>
          登録・編集
        </Button>
      </Box>

      <PeriodFilterChips
        value={periodFilter}
        options={PERIOD_FILTER_OPTIONS}
        counts={filterCounts}
        onChange={setPeriodFilter}
      />

      {notifications.length === 0 ? (
        <Paper>
          <Box className={classes.emptyState}>
            <Typography variant="h6">お知らせが登録されていません</Typography>
          </Box>
        </Paper>
      ) : filteredNotifications.length === 0 ? (
        <Paper>
          <Box className={classes.emptyState}>
            <Typography variant="h6">該当するお知らせがありません</Typography>
          </Box>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table className={classes.table}>
            <TableHead>
              <TableRow>
                <TableCell>公開状態</TableCell>
                <TableCell>ID</TableCell>
                <TableCell>タイトル</TableCell>
                <TableCell>タイプ</TableCell>
                <TableCell>URL</TableCell>
                <TableCell>表示開始日時</TableCell>
                <TableCell>表示終了日時</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredNotifications.map(notification => {
                const status = getNotificationPublishStatus(notification)
                return (
                  <TableRow key={notification.id}>
                    <TableCell>
                      <Chip label={NOTIFICATION_STATUS_LABELS[status]} size="small" />
                    </TableCell>
                    <TableCell>{notification.id}</TableCell>
                    <TableCell>{notification.title}</TableCell>
                    <TableCell>
                      <Chip
                        label={notification.type}
                        size="small"
                        className={notification.type === 'information' ? classes.infoChip : classes.warningChip}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" className={classes.urlCell}>
                        {notification.url}
                      </Typography>
                    </TableCell>
                    <TableCell>{formatDisplayDate(notification.startDisplayAt)}</TableCell>
                    <TableCell>{formatDisplayDate(notification.endDisplayAt)}</TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  )
}
